import { Fragment, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import {
  ChartBar,
  ChartGantt,
  ChartLine,
  ChartNoAxesCombined,
  ChartScatter,
  Compass,
} from "lucide-react";

import type {
  ParametersUnit,
  ParameterTimeSeriesConfiguration,
  ParameterTimeSeriesThreshold,
} from "@/lib/api";
import type { TimeSeriesParameterOption } from "@/features/shared/widgets/map/time-series/time-series-parameter.types";
import { formatUnitLabel } from "@/lib/api/parameter-time-series-configuration.utils";
import { isValidThreshold } from "@/lib/api/parameter-time-series-threshold.utils";
import { resolveThresholdPointColor } from "@/components/graph/threshold-colors";
import { unitDisplaySymbol } from "@/features/shared/widgets/map/time-series/time-series-parameter-option.utils";
import { GraphType } from "@/features/shared/widgets/model-map/make-time-series-configs";
import {
  coerceVisualizationType,
  readVisualizationType,
  seriesParameterIds,
  visualizationTypeToGraphType,
} from "@/features/shared/widgets/model-map/model-map-visualization-types";
import Button from "@/components/button/button";
import Icon from "@/components/icon/icon";
import { DragHandle } from "@/features/admin/components/sortable-list";
import { configItemCardStyles } from "@/features/widget-forms/forms/weather-model-map/components/draggable-card/draggable-card";
import { useTranslation } from "@/lib/i18n";

import styles from "./time-series-config-card.module.css";

interface TimeSeriesConfigCardProps {
  config: ParameterTimeSeriesConfiguration;
  parameters: TimeSeriesParameterOption[];
  onEdit: () => void;
  onRemove: () => void;
}

function TimeSeriesConfigCard({
  config,
  parameters,
  onEdit,
  onRemove,
}: TimeSeriesConfigCardProps) {
  const { t } = useTranslation();
  const seriesCount = config.series.length;
  const unit = config.unit ?? undefined;
  const unitName = formatUnitLabel(unit);
  const thresholds = (config.thresholds ?? []).filter(isValidThreshold);
  const presentation = unit
    ? coerceVisualizationType(
        readVisualizationType(config),
        unit.code,
        seriesParameterIds(config),
      )
    : null;
  const graphType = presentation ? visualizationTypeToGraphType(presentation) : null;
  const visualizationLabel = presentation
    ? t(`admin.widgets.fields.model_map.time_series.visualization_${presentation}`)
    : "";
  const VisualizationIcon: LucideIcon | null =
    graphType === GraphType.LineGraph
      ? ChartLine
      : graphType === GraphType.Histogram
        ? ChartBar
        : graphType === GraphType.Scatterplot
          ? ChartScatter
          : graphType === GraphType.VectorPlot
            ? Compass
            : graphType === GraphType.AvaIndex
              ? ChartNoAxesCombined
              : graphType === GraphType.Pasquill
                ? ChartGantt
                : null;

  const headerSegments: { key: string; node: ReactNode }[] = [];
  if (unitName) {
    headerSegments.push({
      key: "unit",
      node: t("admin.widgets.fields.model_map.time_series.unit_hint", { unit: unitName }),
    });
  }
  if (visualizationLabel && VisualizationIcon) {
    headerSegments.push({
      key: "visualization",
      node: (
        <span className={styles.visualizationWithIcon}>
          <VisualizationIcon size={16} className={styles.parameterIcon} />
          {visualizationLabel}
        </span>
      ),
    });
  }
  if (thresholds.length > 0) {
    headerSegments.push({
      key: "thresholds",
      node: <ThresholdBadges thresholds={thresholds} unit={unit} />,
    });
  }

  return (
    <div className={styles.root}>
      <div className={styles.handleAndContent}>
        <DragHandle />
        <div className={styles.cardContent}>
          <div className={configItemCardStyles.titleGroup}>
            <div className={configItemCardStyles.title}>
              {config.title || t("admin.widgets.fields.model_map.general.untitled")}
            </div>
            {seriesCount > 0 && headerSegments.length > 0 && (
              <div className={styles.unitLine}>
                {headerSegments.map((segment, index) => (
                  <Fragment key={segment.key}>
                    {index > 0 && <span className={styles.derivedSeparator}> · </span>}
                    {segment.node}
                  </Fragment>
                ))}
              </div>
            )}
          </div>
          {seriesCount > 0 ? (
            <div className={styles.parameterBlocks}>
              {config.series.map((s) => (
                <TimeSeriesParameterBlock
                  key={s.parameter_id}
                  series={s}
                  parameters={parameters}
                />
              ))}
            </div>
          ) : (
            <div className={styles.emptyHint}>
              {t("admin.widgets.fields.model_map.time_series.empty_series")}
            </div>
          )}
        </div>
        <div className={styles.actions}>
          <Button
            type="button"
            variant="ghost"
            onClick={onEdit}
            aria-label={t("admin.widgets.fields.model_map.time_series.edit")}
          >
            <Icon name="pencil" size={16} />
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={onRemove}
            aria-label={t("admin.widgets.fields.model_map.general.remove_item", {
              item: config.title,
            })}
          >
            <Icon name="x" size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}

interface TimeSeriesParameterBlockProps {
  series: ParameterTimeSeriesConfiguration["series"][number];
  parameters: TimeSeriesParameterOption[];
}

/** The group unit is shown once in the card header, so blocks omit it. */
function TimeSeriesParameterBlock({ series, parameters }: TimeSeriesParameterBlockProps) {
  const param = parameters.find((p) => p.id === series.parameter_id);
  const catalogLabel = param?.label ?? series.parameter_id;
  return (
    <div className={styles.parameterBlock}>
      <div className={styles.parameterLabel}>{series.label}</div>
      <div className={styles.parameterMeta}>
        <span className={styles.catalogLabel}>{catalogLabel}</span>
      </div>
    </div>
  );
}

interface ThresholdBadgesProps {
  thresholds: ParameterTimeSeriesThreshold[];
  unit: ParametersUnit | undefined;
}

/**
 * Inline summary of the configured thresholds: one rounded badge per threshold,
 * each showing its color, a greater-/less-than symbol (`max` triggers above,
 * `min` below) and the value with the unit symbol for context.
 */
function ThresholdBadges({ thresholds, unit }: ThresholdBadgesProps) {
  const { t } = useTranslation();
  const unitSymbol = unit ? unitDisplaySymbol(unit) : "";

  return (
    <div className={styles.thresholds}>
      {thresholds.map((threshold, index) => {
        const isMax = threshold.threshold_type === "max";
        const symbol = isMax ? "≥" : "≤";
        const value = `${formatThresholdValue(threshold.value)}${unitSymbol}`;
        const directionLabel = t(
          isMax
            ? "admin.widgets.fields.model_map.time_series.thresholds.direction_above"
            : "admin.widgets.fields.model_map.time_series.thresholds.direction_below",
        );
        return (
          <span
            key={`${threshold.color}-${threshold.threshold_type}-${index}`}
            className={styles.thresholdBadge}
            title={t(
              "admin.widgets.fields.model_map.time_series.thresholds.summary_badge_aria",
              { direction: directionLabel, value },
            )}
          >
            <span
              className={styles.thresholdBubble}
              style={{ backgroundColor: resolveThresholdPointColor(threshold.color) }}
            />
            <span className={styles.thresholdRule}>{symbol}</span>
            <span className={styles.thresholdRule}>{value}</span>
          </span>
        );
      })}
    </div>
  );
}

/** Trim float noise from stored values without forcing a fixed precision. */
function formatThresholdValue(value: number): string {
  return String(Math.round(value * 1000) / 1000);
}

export default TimeSeriesConfigCard;
